import * as React from 'react';
import {
    Box, Tab, Tabs, Typography, Alert, Button, Divider,
    FormControlLabel, Radio, RadioGroup, Stack,
    useTheme,
    useMediaQuery,
    Checkbox
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { DisplaySettings, Layers } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { t } from 'i18next';
import { toNumber, isEmpty, set } from 'lodash';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { convertSpeed } from '../../shared/utils/conversion';
import { getTime } from '../../shared/utils/MapSection';

import Graphs from './charts/Graphs';
import IntervalTraining from './charts/IntervalTraining';
import Sectional from './charts/Sectional';
import CheckpointsSection from './CheckpointsSection';
import ChukkersAnalysis from './ChukkersAnalysis';

import { scale } from '../../enums/scale.enum';
import { Scale } from '../../pages/trainings/ExamView';
import {
    CheckPoint, Theats, Ttraining, TTrainingSection
} from '../../shared/types/training.type';
import { TrainingSetting } from '../../shared/types/trainingSetting.type';
import { TPlatformConfiguration } from '../../shared/types/platformConfiguration';
import { Sectional as TSectional } from '../../shared/types/sectionals.type';
import { Chukkers } from '../../shared/types/chukkers.type';
import { TUser } from '../../shared/types/user.type';
import { CreateChukkerDto } from '../../dto/createChukkerDto';
import { RootState } from '../../store';
import { useAuth } from '../../hooks/useAuth';

dayjs.extend(duration);

type TabsProps = {
    scaleOption: Scale;
    onChangeScale: (val: Scale) => void;
    distance: string;
    speed: number[];
    bpm: number[];
    rawDistance: number[];
    time: number[];
    strideFrequency: number[];
    strideLength: number[];
    currentIndex: number;
    flIndex?: number | null;
    loading: boolean;
    isFinishLineActive: boolean;
    heats?: Theats[];
    intervals: TSectional[];
    id?: number;
    editable?: boolean;
    checkPoints: CheckPoint[];
    loCardio: number[];
    percentageGoodCardio: number;
    onIndexChange: (index: number) => void;
    showIntervalTraining: boolean;
    chukkers?: Chukkers;
    saveChukker?: (data: CreateChukkerDto, plotId?: number | null) => void;
    onDeleteChukker?: (id: number) => void;
    setIsChukkerSelectionEnabled: (value: boolean) => void;
    setIsChukkerEditionEnabled: (value: boolean) => void;
    setIsChukkerEditModeEnabled: (value: boolean) => void;
    isChukkerSelectionEnabled: boolean;
    isChukkerEditionEnabled: boolean;
    isChukkerEditModeEnabled: boolean;
    plotBandId: number | null;
    setPlotBandId: (value: number | null) => void;
    editActive: boolean
    setIsEditActive: (value: boolean) => void,
    
};

function TabsGraphs(props: TabsProps) {
    const {
        plotBandId, setPlotBandId, setIsChukkerEditModeEnabled, setIsChukkerEditionEnabled,
        setIsChukkerSelectionEnabled, isChukkerEditModeEnabled, isChukkerEditionEnabled,
        isChukkerSelectionEnabled, onDeleteChukker, saveChukker, chukkers,
        showIntervalTraining, onIndexChange, checkPoints, editable, id, heats,
        scaleOption, onChangeScale, distance, speed, bpm, rawDistance, time,
        strideFrequency, strideLength, currentIndex, flIndex, isFinishLineActive,
        intervals, loCardio, percentageGoodCardio, editActive, setIsEditActive
    } = props;

    const { isUserTypePolo, isUserHasAdaptiveDisplay } = useAuth()

    const [value, setValue] = React.useState('0');
    const [hintValue, setHintValue] = React.useState<string | undefined>('');

    const userSettings = useSelector((state: RootState) => state.user?.user?.training_settings) as TrainingSetting;
    const metric = useSelector((state: RootState) => state.user.user?.unit_system) as 'metric' | 'imperial';
    const platformConfigurations = useSelector((state: RootState) => state.user.platformConfigurations) as TPlatformConfiguration[];

    const [mergedBpmSpeed, setMergeBpmSpeed] = React.useState(userSettings.merge_graphs_speed_bpm)
    const [mergedStride, setMergeStride] = React.useState(userSettings.merge_graphs_stride_frequency_stride_length)

    const theme = useTheme()
    const screenSmall = useMediaQuery(theme.breakpoints.down("sm"));



    const mergeOptions = [
        {
            key: mergedBpmSpeed,
            title: 'settings:mergeGraphsSpeedBpm',
            checkBoxKey: "merge_graphs_speed_bpm",
            onChange: (value: boolean) => setMergeBpmSpeed(value)
        },
        {
            key: mergedStride,
            title: 'settings:mergeGraphsStrideLengthStrideFrequency',
            checkBoxKey: "merge_graphs_stride_frequency_stride_length",
            onChange: (value: boolean) => setMergeStride(value)

        },
    ]

    const displayAlertBadCardio = React.useMemo(() => {
        const threshold = toNumber(platformConfigurations?.find(c => c.key === 'alert_percentage_good_cardio_treshold_bad')?.value) ?? 50;
        return percentageGoodCardio < threshold;
    }, [platformConfigurations, percentageGoodCardio]);

    const onExtremesChanged = React.useCallback((value: number) => {
        if (scaleOption === 'DISTANCE') {
            setHintValue(`${value.toFixed(2)} ${metric === 'metric' ? 'km' : 'mi'}`);
        } else {
            //Convert time to ms to be handled with getTime fn
            const formatted = getTime(value * 60)
            setHintValue(`${formatted}`);
        }
    }, [scaleOption, metric]);

    const handleChukkerSave = React.useCallback((data: CreateChukkerDto, plotId?: number | null) => {
        if (typeof saveChukker === 'function') saveChukker(data, plotId);
    }, [saveChukker]);

    const toggleChukkerEditMode = React.useCallback((value: boolean) => {
        setIsChukkerEditModeEnabled(value);
        setIsChukkerSelectionEnabled(true);
    }, [setIsChukkerEditModeEnabled, setIsChukkerSelectionEnabled]);

    const handleChange = React.useCallback((_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
        const idMap: Record<string, string> = {
            '0': '#graphs-section',
            '1': '#intervalTraining-section',
            '2': '#sectional-section',
            '3': '#cp-section',
            '4': '#chukker-section'
        };
        const id = idMap[newValue];
        const element = id ? document.querySelector(id) : null;
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const handleChukkerEditClick = React.useCallback(() => {
        const active = !editActive;
        setIsEditActive(active);
        setIsChukkerSelectionEnabled(active);
        setIsChukkerEditionEnabled(active);
        setPlotBandId(null);
    }, [editActive, setIsChukkerSelectionEnabled, setIsChukkerEditionEnabled, setPlotBandId]);


    const isGoodHeats = (heats || []).filter(heat => heat?.is_work).length >= 2

    const filteredTabs = React.useMemo(() => [
        {
            index: '0',
            title: t('forthSection:graph'),
            visible: userSettings?.display_section_graphs,
            value: 'GRAPHS',
            component: (
                <Graphs

                    plotBandId={plotBandId}
                    setPlotBandId={setPlotBandId}
                    onDeleteChukker={onDeleteChukker}
                    editModeEnabled={isChukkerEditModeEnabled}
                    toggleChukkerEditMode={toggleChukkerEditMode}
                    isChukkerEditionEnabled={isChukkerEditionEnabled}
                    chukkers={chukkers}
                    saveChukker={handleChukkerSave}
                    isChukkerSelectionEnabled={isChukkerSelectionEnabled}
                    hintValue={hintValue}
                    onIndexChange={onIndexChange}
                    loCardio={loCardio}
                    onExtremesChanged={onExtremesChanged}
                    flIndex={flIndex}
                    isFinishLineActive={isFinishLineActive}
                    labelsColor={isUserHasAdaptiveDisplay ? "#222" : undefined}
                    currentIndex={currentIndex}
                    speed={speed}
                    bpm={bpm}
                    distance={rawDistance}
                    scaleOption={scaleOption}
                    time={time}
                    strideFrequency={strideFrequency}
                    strideLength={strideLength}
                    displayAlertBadCardio={displayAlertBadCardio}
                    mergedBpmSpeed={mergedBpmSpeed}
                    mergedStride={mergedStride}
                    adaptiveDisplay={isUserHasAdaptiveDisplay}
                />
            )
        },
        {
            index: '1',
            title: t('intervalTrainingAnalysis:title'),
            value: 'ITA',
            visible: showIntervalTraining && userSettings?.display_section_interval_training_analysis && isGoodHeats && !isUserTypePolo,
            component: (
                <IntervalTraining
                    flIndex={flIndex}
                    isFinishLineActive={isFinishLineActive}
                    distance={rawDistance}
                    time={time}
                    heats={heats}
                    speed={speed}
                    bpm={bpm}
                    scaleOption={scaleOption}
                    labelsColor={isUserHasAdaptiveDisplay ? "#222" : undefined}
                    adaptiveDisplay={isUserHasAdaptiveDisplay}
                />
            )
        },
        {
            index: '2',
            title: t('intervals:title'),
            value: 'SECTIONALS',
            visible: userSettings?.display_section_intervals && !isUserTypePolo,
            component: (
                <Sectional
                    focusOnMainWork={userSettings?.sectionals_focus_main_work}
                    isFinishLineActive={isFinishLineActive}
                    editable={editable}
                    time={time}
                    currentIndex={currentIndex}
                    intervals={intervals}
                    id={id}
                />
            )
        },
        {
            index: '4',
            title: t('chukkers:title'),
            value: 'CHUKKERS',
            visible: isUserTypePolo,
            component: <ChukkersAnalysis chukkers={chukkers} />
        },
        {
            index: '3',
            title: t('checkpointsCrossings:title'),
            value: 'CP',
            visible: userSettings?.display_section_checkpoints && !isEmpty(checkPoints),
            component: (
                <CheckpointsSection
                    onChangeScale={onChangeScale}
                    scaleOption={scaleOption}
                    isFinishlineActive={isFinishLineActive}
                    flIndex={flIndex}
                    checkPoints={checkPoints}
                    time={time}
                    rawDistance={rawDistance}
                    speed={speed}
                    bpm={bpm}
                    labelsColor={isUserHasAdaptiveDisplay ? "#222" : undefined}
                    adaptiveDisplay={isUserHasAdaptiveDisplay}

                />
            )
        },

    ].filter(tab => tab.visible), [
        plotBandId, setPlotBandId, onDeleteChukker, isChukkerEditModeEnabled,
        toggleChukkerEditMode, isChukkerEditionEnabled, chukkers, isUserTypePolo,
        handleChukkerSave, isChukkerSelectionEnabled, hintValue, onIndexChange,
        loCardio, onExtremesChanged, flIndex, isFinishLineActive, currentIndex,
        speed, bpm, rawDistance, scaleOption, time, strideFrequency, strideLength,
        displayAlertBadCardio, showIntervalTraining, userSettings, heats,
        editable, intervals, id, checkPoints, onChangeScale, screenSmall, isGoodHeats, mergedBpmSpeed, mergedStride
    ]);



    return (
        <Box sx={{ width: '100%', typography: 'body1', overflowX: 'auto' }}>
            <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
                        {filteredTabs.map(tab => (
                            <Tab key={tab.index} label={tab.title} value={tab.index} />
                        ))}
                    </Tabs>
                </Box>

                <TabPanel value={value} sx={{ mt: 1 }}>
                    <Box display="flex" alignItems="center" gap={2} justifyContent="space-between" mt={2} flexWrap={'wrap'}>
                        <Box>
                            {isUserTypePolo && (
                                <Button onClick={handleChukkerEditClick}
                                    variant={editActive ? 'contained' : 'outlined'}>
                                    {t('chukkers:editChukker')}
                                </Button>
                            )}
                        </Box>


                        {/* Scale and settiongs Card */}

                        <Box display={'flex'} gap={2} flexDirection={'row'} flexWrap={'wrap'} alignItems={'center'} bgcolor="var(--lightGray)" borderRadius={3} p={2} mb={1}>

                            {
                                isUserTypePolo &&
                                <>
                                    <Box display={'flex'} flexDirection={'column'}>
                                        <Typography variant='caption' color={'#808080'} display={'flex'} alignItems={'center'} gap={'3px'}>
                                            <DisplaySettings sx={{ width: 14, height: 14 }}></DisplaySettings>
                                            {t('settings:title')}</Typography>
                                        <Box display={'flex'} alignItems={'center'}>
                                            {
                                                mergeOptions.map(option => (
                                                    <FormControlLabel control={<Checkbox size='small' />} onChange={(_, checked) => option.onChange(checked)} checked={option.key} key={option.title} label={<Typography variant="caption" fontWeight={500}>{t(option.title)}</Typography>} />

                                                ))
                                            }
                                        </Box>

                                    </Box>
                                    <Divider flexItem sx={{ display: screenSmall ? 'none' : 'initial' }} orientation={'vertical'} />
                                </>
                            }

                            {/* Scale Section */}
                            <Box display={'flex'} flexDirection={'column'} >
                                <Typography variant="caption" color="GrayText" display="flex" alignItems="center" gap="3px">
                                    <Layers sx={{ width: 14, height: 14 }} />
                                    {t(!editable ? 'graph:scale' : 'settings:defaultGraphUnitType')}
                                </Typography>
                                <RadioGroup row>
                                    {scale.map(s => (
                                        <FormControlLabel
                                            key={s.value}
                                            value={s.value}
                                            checked={scaleOption === s.value}
                                            control={
                                                <Radio
                                                    onChange={e => onChangeScale(e.target.value as Scale)}
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="caption" fontWeight={500}>{t(s.key)}</Typography>}
                                        />
                                    ))}
                                </RadioGroup>
                            </Box>

                        </Box>
                    </Box>

                    {editActive && (
                        <Box mb={2}>
                            <Alert severity="info">{t('chukkers:editHint')}</Alert>
                        </Box>
                    )}

                    <Stack direction="column" spacing={2}>
                        {filteredTabs.map(tab => (
                            <React.Fragment key={tab.index}>
                                {tab.value !== 'GRAPHS' && <Typography variant="h6">{tab.title}</Typography>}
                                {tab.component}
                                <Divider />
                            </React.Fragment>
                        ))}
                    </Stack>
                </TabPanel>
            </TabContext>
        </Box>
    );
}

export default React.memo(TabsGraphs);
